import { debounce } from 'lodash';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchPatientQueryFilter } from 'infrastructure/hooks/ccm/console';
import { removeEmptyValuesFromObject } from 'utils/object';

import { options, useSearchPatient } from './use-search-patient';

const DEFAULT_FILTER = options[0];

export const useSearch = () => {
  const [searchKey, setSearchKey] = useState(DEFAULT_FILTER.value);
  const [searchValue, setSearchValue] = useState('');
  const { filter, updateFilter } = useSearchPatientQueryFilter();

  const { search, dataOptions, isLoading } = useSearchPatient();

  const placeholder = useMemo(
    () => options.find((o) => o.value === searchKey)?.placeholder,
    [searchKey],
  );
  const debounceSearch = useCallback(debounce(search, 800), []);

  const appendFilters = (filtersToAppend: any) => {
    const newFilters = removeEmptyValuesFromObject({
      ...filter,
      ...filtersToAppend,
    });
    updateFilter(newFilters);
  };

  const onSelect = (
    name: string,
    option: {
      value: string;
      label: string;
    },
  ) => {
    const { label, value } = option;
    updateFilter({
      patientGuid: value,
      'f-page': undefined,
      'n-page': undefined,
      'p-page': undefined,
    });
    setSearchValue(label);
  };

  const onClear = () => {
    updateFilter({
      patientGuid: undefined,
      'f-page': undefined,
      'n-page': undefined,
      'p-page': undefined,
    });
    setSearchValue('');
  };

  const onTypeChange = (v: typeof searchKey) => {
    updateFilter({
      patientGuid: undefined,
      'f-page': undefined,
      'n-page': undefined,
      'p-page': undefined,
    });
    setSearchValue('');
    setSearchKey(v);
  };

  useEffect(() => {
    setSearchValue('');
  }, [searchKey]);

  useEffect(() => {
    if (filter.patientGuid) {
      search(
        { searchKey: 'guid', searchValue: filter.patientGuid },
        {
          onSuccess(data) {
            if (data[0]) {
              setSearchValue(data[0].label);
              setSearchKey('fullName');
            }
          },
        },
      );
    }
  }, []);

  return {
    debounceSearch,
    dataOptions,
    isLoading,
    placeholder,
    options,
    DEFAULT_FILTER,
    searchValue,
    searchKey,
    setSearchKey,
    setSearchValue,
    onSelect,
    onClear,
    onTypeChange,
    appendFilters,
    ccmCaseManagerGuid: filter.ccmCaseManagerGuid,
  };
};
